import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Target,
  Trophy,
  DollarSign,
  TrendingUp,
  Shield,
  Zap,
  Star,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  BarChart3,
  Activity,
  Clock,
  Award,
  Layers,
  X,
  Wallet
} from 'lucide-react';
import FuturesTradingForms from './FuturesTradingForms';
import CFDTradingForms from './CFDTradingForms';
import FuturesMyOrders from './FuturesMyOrders';
import SearchablePairSelector from './SearchablePairSelector';
import TradingChart from './TradingChart';
import { DatabaseFuturesPosition, MarketData, DatabaseRobotState } from '../hooks/useDatabase';
import { supabase } from '../lib/supabaseClient';
import { usePropFirmTrading } from '../hooks/usePropFirmTrading';
import { usePrevious } from '../hooks/usePrevious';
import { useMarketData } from '../contexts/MarketDataContext';
import { Editorial, BorderRadius, Typography } from '../styles/primitives';

// Calculate total PnL from position history
const calculateTotalHistoricalPnl = (positionHistory: any[]) => {
  return positionHistory.reduce((total, position) => {
    return total + (parseFloat(position.pnl) || 0);
  }, 0);
};

interface Challenge {
  id: string;
  name: string;
  cost: number;
  accountSize: number;
  profitTarget: number;
  maxDrawdown: number;
  timeLimit: number; // in days
  description: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert' | 'Elite' | 'Master';
  color: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
}

interface TickerData {
  price: number;
  change24h: number;
  volume24h: number;
}

interface PropFirmChallengePageProps {
  currentPrice: number;
  usdtBalance: number;
  selectedPair: string;
  setSelectedPair: (pair: string) => void;
  polygonData?: MarketData[];
  yahooData?: MarketData[];
  robotState: DatabaseRobotState | null;
  updateBalances: (updates: { usdt_balance?: number, transaction?: any }) => void;
  addTransaction: (transaction: any) => void;
  updateRobotState: (updates: Partial<DatabaseRobotState>) => void;
  onClosePropPosition: (positionId: string) => Promise<boolean>;
  onCancelPropOrder: (orderId: string) => Promise<boolean>;
  onCancelAllPropOrders: () => Promise<boolean>;
}

const PropFirmChallengePage: React.FC<PropFirmChallengePageProps> = ({
  currentPrice,
  usdtBalance,
  selectedPair,
  setSelectedPair,
  polygonData = [],
  yahooData = [],
  robotState,
  updateBalances,
  addTransaction,
  updateRobotState,
  onClosePropPosition,
  onCancelPropOrder,
  onCancelAllPropOrders
}) => {
  const { t } = useTranslation();
  const { marketData, isConnected: realtimeConnected, getSnapshotPriceBySymbol, getMarketDataBySymbol } = useMarketData();

  // Use the prop firm trading hook - must be called early to initialize variables
  const {
    propPositions,
    propOrders,
    propPositionHistory,
    propChallengeAccount,
    fetchPropPositions,
    fetchPropOrders,
    fetchPropPositionHistory,
    fetchPropChallengeAccount,
    initializeChallenge,
    cancelChallenge,
    placePropOrder,
    closePropPosition,
    cancelPropOrder,
    cancelAllPropOrders,
    closeAllPropPositions,
    calculateLiquidationPrice,
    triggerPropOrderProcessing,
    triggerImmediateOrderProcessing
  } = usePropFirmTrading();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingChallenge, setPendingChallenge] = useState<Challenge | null>(null);
  const [tradingMode, setTradingMode] = useState<'crypto' | 'cfd'>('crypto');
  const [lastSelectedPair, setLastSelectedPair] = useState<{crypto: string, cfd: string}>({
    crypto: 'BTCUSDT',
    cfd: 'AAPL'
  });

  // Use a ref to store previous prices for each symbol
  const previousPricesRef = useRef<Record<string, number>>({});
  const [showChallengeOutcomeModal, setShowChallengeOutcomeModal] = useState(false);
  const [challengeOutcome, setChallengeOutcome] = useState<{
    status: 'won' | 'lost' | 'expired' | 'cancelled';
    message: string;
    profit: number;
  } | null>(null);
  const [isClosingChallenge, setIsClosingChallenge] = useState(false);
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const challenges: Challenge[] = [
    {
      id: 'starter',
      name: 'Starter Challenge',
      cost: 150,
      accountSize: 10000,
      profitTarget: 800, // 8%
      maxDrawdown: 500, // 5%
      timeLimit: 30,
      description: 'Perfect for beginners to test their trading skills',
      difficulty: 'Beginner',
      color: 'from-green-500 to-emerald-600',
      icon: Shield
    },
    {
      id: 'bronze',
      name: 'Bronze Challenge',
      cost: 300,
      accountSize: 25000,
      profitTarget: 2000, // 8%
      maxDrawdown: 1250, // 5%
      timeLimit: 30,
      description: 'Step up your game with a larger account',
      difficulty: 'Intermediate',
      color: 'from-amber-600 to-orange-600',
      icon: Trophy
    },
    {
      id: 'silver',
      name: 'Silver Challenge',
      cost: 600,
      accountSize: 50000,
      profitTarget: 4000, // 8%
      maxDrawdown: 2500, // 5%
      timeLimit: 30,
      description: 'Serious trading with professional tools',
      difficulty: 'Advanced',
      color: 'from-slate-400 to-slate-600',
      icon: Star
    },
    {
      id: 'gold',
      name: 'Gold Challenge',
      cost: 1200,
      accountSize: 100000,
      profitTarget: 8000, // 8%
      maxDrawdown: 5000, // 5%
      timeLimit: 30,
      description: 'Elite level trading challenge',
      difficulty: 'Expert',
      color: 'from-yellow-500 to-amber-600',
      icon: Award
    },
    {
      id: 'platinum',
      name: 'Platinum Challenge',
      cost: 2400,
      accountSize: 200000,
      profitTarget: 16000, // 8%
      maxDrawdown: 10000, // 5%
      timeLimit: 30,
      description: 'For experienced professional traders',
      difficulty: 'Elite',
      color: 'from-purple-500 to-indigo-600',
      icon: Zap
    },
    {
      id: 'diamond',
      name: 'Diamond Challenge',
      cost: 4800,
      accountSize: 400000,
      profitTarget: 32000, // 8%
      maxDrawdown: 20000, // 5%
      timeLimit: 30,
      description: 'The ultimate trading challenge for masters',
      difficulty: 'Master',
      color: 'from-cyan-500 to-blue-600',
      icon: Layers
    }
  ];

  // Get the current active challenge from robotState
  const getActiveChallenge = React.useCallback(() => {
    // First check if there's an active challenge in robotState
    if (robotState?.active_challenge_id) {
      const challenge = challenges.find(c => c.id === robotState.active_challenge_id);
      if (challenge) return challenge;
    }

    // Return null if no active challenge is found
    return null;
  }, [robotState]);

  const selectedChallenge = React.useMemo(() => getActiveChallenge(), [getActiveChallenge]);
  const challengeAccountBalance = robotState?.challenge_account_balance || 0;

  // Set default pair when trading mode changes
  useEffect(() => {
    // When trading mode changes, set the appropriate pair for that mode
    if (tradingMode === 'crypto') {
      // If current pair isn't a crypto pair, set to the last selected crypto pair
      if (!selectedPair.endsWith('USDT')) {
        setSelectedPair(lastSelectedPair.crypto);
      }
    } else if (tradingMode === 'cfd') {
      // If current pair isn't a CFD instrument, set to the last selected CFD pair
      if (selectedPair.endsWith('USDT')) {
        setSelectedPair(lastSelectedPair.cfd);
      }
    }
  }, [tradingMode]);

  // Save the last selected pair for each mode
  useEffect(() => {
    if (selectedPair.endsWith('USDT')) {
      setLastSelectedPair(prev => ({...prev, crypto: selectedPair}));
    } else {
      setLastSelectedPair(prev => ({...prev, cfd: selectedPair}));
    }
  }, [selectedPair]);

  // Check for challenge outcome when robotState changes
  useEffect(() => {
    // Guard against undefined robotState
    if (!robotState) return;

    const currentChallenge = getActiveChallenge();
    if (robotState?.challenge_status && robotState.challenge_status !== 'active' && currentChallenge) {
      // Calculate profit
      const profit = robotState.challenge_account_balance - robotState.challenge_initial_balance;

      // Set challenge outcome
      setChallengeOutcome({
        status: robotState.challenge_status as 'won' | 'lost' | 'expired' | 'cancelled',
        message: robotState.challenge_status === 'won'
          ? `Congratulations! You've reached the profit target of $${currentChallenge.profitTarget}.`
          : robotState.challenge_status === 'lost'
          ? `Challenge lost. You've exceeded the maximum drawdown of $${currentChallenge.maxDrawdown}.`
          : robotState.challenge_status === 'expired'
          ? `Challenge expired. The ${currentChallenge.timeLimit} day time limit has been reached.`
          : 'Challenge cancelled.',
        profit
      });

      // Show outcome modal
      setShowChallengeOutcomeModal(true);
    }
  }, [robotState?.challenge_status, robotState?.active_challenge_id]);

  // Handle closing the challenge outcome modal and resetting the challenge
  const handleCloseOutcomeModal = async () => {
    setIsClosingChallenge(true);
    setIsCleaningUp(true);
    try {
      // Use the same exitChallenge function for consistency
      await exitChallenge();

        setSuccessMessage('Challenge exited successfully');
      // Close the modal
      setShowChallengeOutcomeModal(false);
      setChallengeOutcome(null);
    } catch (error) {
      setErrorMessage('Failed to exit challenge. Please try again.');
    } finally {
      // These states are already reset in exitChallenge()
    }
  };

  // Calculate real-time PnL and ROI for each position
  const propPositionsWithLiveData = useMemo(() => {
    return propPositions.map(position => {
      // Initialize livePrice to 0 (will be populated from the best available source)
      let livePrice = 0;
      const symbol = position.symbol;

      // Use market data price if available
      if (marketData.find(data => data.symbol === symbol)?.price) {
        livePrice = marketData.find(data => data.symbol === symbol)!.price;
      }
      // Fall back to position's current price
      else if (position.current_price) {
        livePrice = position.current_price;
      }
      // Last resort: Use entry price if nothing else is available
      else if (position.entry_price) {
        livePrice = position.entry_price;
      }

      // Calculate unrealized PnL based on live price
      let unrealizedPnl = 0;
      if (position.side === 'long') {
        unrealizedPnl = (livePrice - position.entryPrice) * position.amount;
      } else {
        unrealizedPnl = (position.entryPrice - livePrice) * position.amount;
      }

      // Calculate ROI
      const roi = position.margin > 0 ? (unrealizedPnl / position.margin) * 100 : 0;

      // Determine flash class based on price change
      let flashClass = '';
      const prevPrice = previousPricesRef.current[symbol];
      if (prevPrice && livePrice !== prevPrice) {
        flashClass = livePrice > prevPrice ? 'animate-flash-green' : 'animate-flash-red';
      }

      return {
        ...position,
        currentPrice: livePrice,
        unrealizedPnl,
        roi,
        flashClass
      };
    });
  }, [propPositions, marketData]);

  // After calculating positions with live data, update previous prices ref
  useEffect(() => {
    if (propPositionsWithLiveData.length > 0) {
      const newPrices: Record<string, number> = {};
      propPositionsWithLiveData.forEach(position => {
        if (position.symbol && position.currentPrice > 0) {
          newPrices[position.symbol] = position.currentPrice;
        }
      });
      previousPricesRef.current = {...previousPricesRef.current, ...newPrices};
    }
  }, [propPositionsWithLiveData]);

  // Fetch prop positions when challenge is active
  useEffect(() => {
    const fetchPropData = async () => {
      if (selectedChallenge) {
        try {
          // Fetch prop positions and orders for this challenge
          const challengeId = selectedChallenge.id;
          await fetchPropChallengeAccount(challengeId);
          await fetchPropPositions(challengeId);
          await fetchPropOrders(challengeId);
          await fetchPropPositionHistory(challengeId);
        } catch (err) {
          console.error('Error fetching prop data:', err);
        }
      }
    };

    fetchPropData();

    // Set up interval to refresh data
    const interval = setInterval(fetchPropData, 5000);
    return () => clearInterval(interval);
  }, [selectedChallenge, fetchPropPositions, fetchPropOrders, fetchPropChallengeAccount]);

  // Clear error and success messages after 5 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Calculate total unrealized PnL from all positions
  const totalUnrealizedPnl = propPositionsWithLiveData.reduce((sum, position) => sum + (position.unrealizedPnl || 0), 0);

  // Calculate total margin used from all positions
  const totalMarginUsed = propPositions.reduce((sum, position) => sum + (parseFloat(position.margin) || 0), 0);

  // Calculate total PnL from closed positions - ensure we're using the numeric pnl value
  const totalClosedPnl = propPositionHistory.reduce((sum, position) => {
    const pnlValue = typeof position.pnl === 'number' ? position.pnl : parseFloat(position.pnl);
    return sum + (isNaN(pnlValue) ? 0 : pnlValue);
  }, 0);

  // Total PnL is the sum of unrealized PnL from open positions and realized PnL from closed positions
  const totalPnl = totalUnrealizedPnl + totalClosedPnl;

  const handleSelectChallenge = (challenge: Challenge) => {
    setPendingChallenge(challenge);
    setShowConfirmModal(true);
  };

  const confirmChallenge = async () => {
    if (!pendingChallenge) return;

    if (pendingChallenge.cost > usdtBalance) {
      setErrorMessage(`Insufficient balance to start this challenge. Required: ${pendingChallenge.cost} USDT, Available: ${usdtBalance.toFixed(2)} USDT`);
      return;
    }

    // Deduct cost from main balance
    await updateBalances({ usdt_balance: usdtBalance - pendingChallenge.cost });

    // Set up challenge in robotState
    await updateRobotState({
      active_challenge_id: pendingChallenge.id,
      challenge_initial_balance: pendingChallenge.accountSize,
      challenge_account_balance: pendingChallenge.accountSize,
      challenge_profit_target: pendingChallenge.profitTarget,
      challenge_max_drawdown: pendingChallenge.maxDrawdown,
      challenge_time_limit: pendingChallenge.timeLimit,
      challenge_start_date: new Date().toISOString(),
      challenge_status: 'active'
    });

    // Close modal
    setShowConfirmModal(false);
    setPendingChallenge(null);
  };

  const exitChallenge = async () => {
    if (selectedChallenge) {
      setIsCleaningUp(true);
      setIsClosingChallenge(true);

      // Call the proper cancelChallenge function from usePropFirmTrading hook
      // This will handle all the database cleanup via the Edge Function
      const challengeId = selectedChallenge.id;

      try {
        const success = await cancelChallenge(challengeId);

        if (success) {
          // Update challenge state in robotState to cancelled
          await updateRobotState({
            active_challenge_id: null,
            challenge_account_balance: 0,
            challenge_profit_target: 0,
            challenge_max_drawdown: 0,
            challenge_time_limit: null,
            challenge_initial_balance: 0,
            challenge_start_date: null,
            challenge_status: 'cancelled'
          });
        } else {
          throw new Error('Failed to cancel challenge via Edge Function');
        }
      } catch (error) {
        console.error('Error calling cancelChallenge:', error);
        alert('Failed to exit challenge. Please try again.');
      } finally {

        // Reset states
        setIsClosingChallenge(false);
        setIsCleaningUp(false);
      }
    }
  };

  const handleChallengeTrade = async (symbol: string, side: string, amount: number, leverage: number, marginType: string, stopLoss?: any, takeProfit?: any, orderType: string = 'market', price?: number) => {
    if (!selectedChallenge) {
      setErrorMessage('No active challenge found');
      return;
    }

    const currentBalance = propChallengeAccount?.currentBalance || challengeAccountBalance;
    const requiredMargin = (amount * currentPrice) / leverage;

    if (requiredMargin > currentBalance) {
      setErrorMessage(`Insufficient challenge account balance. Required: ${requiredMargin.toFixed(2)} USDT, Available: ${currentBalance.toFixed(2)} USDT`);
      return;
    }

    try {
      const success = await placePropOrder({
        challengeId: selectedChallenge.id,
        symbol: symbol,
        side: side === 'long' ? 'buy' : 'sell',
        amount,
        leverage,
        marginType,
        orderType: orderType,
        price: orderType === 'limit' ? price : undefined,
        stopLoss: stopLoss?.trigger_price,
        takeProfit: takeProfit?.trigger_price
      });

      if (success) {
        setSuccessMessage(`${side === 'long' ? 'Long' : 'Short'} position opened successfully for ${amount} ${symbol}`);
      } else {
        console.error('Failed to place prop order');
        setErrorMessage('Failed to place order. Please try again.');
      }

      // Trigger immediate processing
      await triggerPropOrderProcessing();
    } catch (err) {
      console.error('Error creating prop order:', err);
      setErrorMessage('Failed to place order. Please try again.');
    }
  };

  // Calculate progress metrics
  const progress = useMemo(() => {
    const currentBalance = propChallengeAccount?.currentBalance || challengeAccountBalance;
    const initialBalance = propChallengeAccount?.startingBalance || selectedChallenge?.accountSize || 0;
    const profit = currentBalance - initialBalance;
    const profitPercentage = initialBalance > 0 ? (profit / initialBalance) * 100 : 0;

    // Calculate drawdown (negative profit from peak)
    const drawdown = Math.max(0, -profit);
    const drawdownPercentage = initialBalance > 0 ? (drawdown / initialBalance) * 100 : 0;

    return {
      currentBalance,
      initialBalance,
      profit,
      profitPercentage,
      drawdown,
      drawdownPercentage
    };
  }, [propChallengeAccount, challengeAccountBalance, selectedChallenge]);

  if (!selectedChallenge) {
    return (
      <div
        className="min-h-screen px-20 py-16 bg-slate-50/95"

      >
        {/* Header Section */}
        <div className="max-w-6xl mx-auto mb-20">
          <div className="mb-8 pb-8" style={{ borderBottom: `2px solid ${Editorial.Ink}` }}>
            <h1
              style={{
                fontSize: Typography.DisplayLarge,
                fontWeight: Typography.WeightBold,
                color: '#000000',
                letterSpacing: '-0.04em',
                marginBottom: '16px'
              }}
            >
              {t('propFirm.title')}
            </h1>
            <p
              style={{
                fontSize: Typography.HeadlineSmall,
                color: 'rgba(0, 0, 0, 0.7)',
                lineHeight: '1.5',
                maxWidth: '800px'
              }}
            >
              {t('propFirm.subtitle')}
            </p>
          </div>

          {/* Available Balance - Editorial Style */}
          <div className="mb-16">
            <div
              style={{
                fontSize: Typography.Caption,
                color: 'rgba(0, 0, 0, 0.6)',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                fontWeight: Typography.WeightBold,
                marginBottom: '8px'
              }}
            >
              {t('wallet.availableBalance')}
            </div>
            <div
              className="font-mono"
              style={{
                fontSize: Typography.DisplayMedium,
                fontWeight: Typography.WeightBold,
                color: '#000000'
              }}
            >
              ${usdtBalance.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Challenges - Card Grid Layout */}
        <div className="max-w-6xl mx-auto mb-32">
          <div className="grid grid-cols-3 gap-6">
            {challenges.map((challenge) => {
              const Icon = challenge.icon;
              const canAfford = challenge.cost <= usdtBalance;

              return (
                <div
                  key={challenge.id}
                  className="border group relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    borderColor: 'rgba(0, 0, 0, 0.06)',
                    borderRadius: '20px',
                    opacity: canAfford ? 1 : 0.4,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: canAfford ? 'pointer' : 'not-allowed'
                  }}
                  onMouseEnter={(e) => {
                    if (canAfford) {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.12)';
                      e.currentTarget.style.borderColor = 'rgba(217, 18, 34, 0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (canAfford) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                      e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.06)';
                    }
                  }}
                >
                  {/* Top accent stripe */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: 'linear-gradient(90deg, #d91222 0%, #f97316 100%)'
                    }}
                  />

                  {/* Decorative background pattern */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '-50%',
                      right: '-20%',
                      width: '200px',
                      height: '200px',
                      borderRadius: '50%',
                      background: 'radial-gradient(circle, rgba(217, 18, 34, 0.05) 0%, transparent 70%)',
                      pointerEvents: 'none'
                    }}
                  />

                  <div style={{ padding: '24px', position: 'relative', zIndex: 1 }}>
                    {/* Icon and Title Section */}
                    <div className="flex items-start justify-between mb-5">
                      <div className="flex items-center gap-4">
                        {/* Prominent Icon Container */}
                        <div
                          style={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '16px',
                            background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.1) 0%, rgba(249, 115, 22, 0.1) 100%)',
                            border: '2px solid rgba(217, 18, 34, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Icon size={28} style={{ color: '#d91222' }} />
                        </div>

                        <div>
                          <h3
                            style={{
                              fontSize: Typography.HeadlineMedium,
                              fontWeight: Typography.WeightBold,
                              color: '#000000',
                              letterSpacing: '-0.02em',
                              marginBottom: '4px'
                            }}
                          >
                            {challenge.name}
                          </h3>

                          {/* Difficulty Badge */}
                          <div
                            style={{
                              display: 'inline-block',
                              padding: '4px 12px',
                              borderRadius: '12px',
                              background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.08) 100%)',
                              border: '1px solid rgba(0, 0, 0, 0.1)',
                              fontSize: Typography.Fine,
                              color: 'rgba(0, 0, 0, 0.7)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.08em',
                              fontWeight: Typography.WeightBold
                            }}
                          >
                            {challenge.difficulty}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <p
                      style={{
                        fontSize: Typography.BodySmall,
                        color: 'rgba(0, 0, 0, 0.7)',
                        lineHeight: '1.7',
                        marginBottom: '24px'
                      }}
                    >
                      {challenge.description}
                    </p>

                    {/* Challenge Details - Premium Card Style */}
                    <div
                      style={{
                        background: 'rgba(0, 0, 0, 0.02)',
                        borderRadius: '16px',
                        padding: '20px',
                        marginBottom: '24px',
                        border: '1px solid rgba(0, 0, 0, 0.05)'
                      }}
                    >
                      <div className="space-y-4">
                        {/* Entry Fee */}
                        <div className="flex justify-between items-center pb-3" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
                          <div className="flex items-center gap-2">
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#d91222' }} />
                            <span
                              style={{
                                fontSize: Typography.Caption,
                                color: 'rgba(0, 0, 0, 0.6)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                fontWeight: Typography.WeightMedium
                              }}
                            >
                              {t('propFirm.entryFee')}
                            </span>
                          </div>
                          <span
                            className="font-mono"
                            style={{
                              fontSize: Typography.BodyMedium,
                              fontWeight: Typography.WeightBold,
                              color: '#d91222'
                            }}
                          >
                            ${challenge.cost.toLocaleString()}
                          </span>
                        </div>

                        {/* Account Size */}
                        <div className="flex justify-between items-center pb-3" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
                          <div className="flex items-center gap-2">
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#3b82f6' }} />
                            <span
                              style={{
                                fontSize: Typography.Caption,
                                color: 'rgba(0, 0, 0, 0.6)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                fontWeight: Typography.WeightMedium
                              }}
                            >
                              {t('propFirm.accountSize')}
                            </span>
                          </div>
                          <span
                            className="font-mono"
                            style={{
                              fontSize: Typography.BodyMedium,
                              fontWeight: Typography.WeightBold,
                              color: '#000000'
                            }}
                          >
                            ${challenge.accountSize.toLocaleString()}
                          </span>
                        </div>

                        {/* Profit Target */}
                        <div className="flex justify-between items-center pb-3" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
                          <div className="flex items-center gap-2">
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#16a34a' }} />
                            <span
                              style={{
                                fontSize: Typography.Caption,
                                color: 'rgba(0, 0, 0, 0.6)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                fontWeight: Typography.WeightMedium
                              }}
                            >
                              {t('propFirm.profitTarget')}
                            </span>
                          </div>
                          <span
                            className="font-mono"
                            style={{
                              fontSize: Typography.BodyMedium,
                              fontWeight: Typography.WeightBold,
                              color: '#16a34a'
                            }}
                          >
                            ${challenge.profitTarget.toLocaleString()}
                          </span>
                        </div>

                        {/* Max Drawdown */}
                        <div className="flex justify-between items-center pb-3" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
                          <div className="flex items-center gap-2">
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#f97316' }} />
                            <span
                              style={{
                                fontSize: Typography.Caption,
                                color: 'rgba(0, 0, 0, 0.6)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                fontWeight: Typography.WeightMedium
                              }}
                            >
                              {t('propFirm.maxDrawdown')}
                            </span>
                          </div>
                          <span
                            className="font-mono"
                            style={{
                              fontSize: Typography.BodyMedium,
                              fontWeight: Typography.WeightBold,
                              color: '#000000'
                            }}
                          >
                            ${challenge.maxDrawdown.toLocaleString()}
                          </span>
                        </div>

                        {/* Time Limit */}
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#8b5cf6' }} />
                            <span
                              style={{
                                fontSize: Typography.Caption,
                                color: 'rgba(0, 0, 0, 0.6)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                fontWeight: Typography.WeightMedium
                              }}
                            >
                              {t('propFirm.timeLimit')}
                            </span>
                          </div>
                          <span
                            className="font-mono"
                            style={{
                              fontSize: Typography.BodyMedium,
                              fontWeight: Typography.WeightBold,
                              color: '#000000'
                            }}
                          >
                            {challenge.timeLimit}d
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Insufficient Balance Warning */}
                    {!canAfford && (
                      <div
                        style={{
                          marginBottom: '16px',
                          padding: '12px 16px',
                          background: 'rgba(217, 18, 34, 0.05)',
                          border: '1px solid rgba(217, 18, 34, 0.2)',
                          borderRadius: '12px',
                          fontSize: Typography.BodySmall,
                          color: '#d91222',
                          fontWeight: Typography.WeightMedium
                        }}
                      >
                        {t('propFirm.insufficientBalance')}
                      </div>
                    )}

                    {/* Premium CTA Button */}
                    <button
                      onClick={() => canAfford && handleSelectChallenge(challenge)}
                      disabled={!canAfford}
                      className="w-full py-4 transition-all duration-300 group-button relative overflow-hidden"
                      style={{
                        background: canAfford
                          ? 'linear-gradient(135deg, #d91222 0%, #f97316 100%)'
                          : 'rgba(0, 0, 0, 0.1)',
                        color: '#FFFFFF',
                        fontSize: Typography.BodyMedium,
                        fontWeight: Typography.WeightBold,
                        border: 'none',
                        borderRadius: '16px',
                        cursor: canAfford ? 'pointer' : 'not-allowed',
                        boxShadow: canAfford ? '0 4px 12px rgba(217, 18, 34, 0.3)' : 'none',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}
                      onMouseEnter={(e) => {
                        if (canAfford) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 6px 20px rgba(217, 18, 34, 0.4)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (canAfford) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(217, 18, 34, 0.3)';
                        }
                      }}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <span>Start Challenge</span>
                        <ArrowRight size={18} />
                      </div>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* How It Works - Editorial Section */}
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 pb-6" style={{ borderBottom: `1px solid ${Editorial.Ink}` }}>
            <h2
              style={{
                fontSize: Typography.HeadlineLarge,
                fontWeight: Typography.WeightBold,
                color: '#000000',
                letterSpacing: '-0.02em'
              }}
            >
              {t('propFirm.howItWorks')}
            </h2>
          </div>

          <div className="grid grid-cols-3 gap-16">
            <div>
              <div
                style={{
                  fontSize: Typography.HeadlineSmall,
                  fontWeight: Typography.WeightBold,
                  color: '#000000',
                  marginBottom: '8px'
                }}
              >
                1. {t('propFirm.payEntryFee')}
              </div>
              <p
                style={{
                  fontSize: Typography.BodyMedium,
                  color: 'rgba(0, 0, 0, 0.7)',
                  lineHeight: '1.65'
                }}
              >
                {t('propFirm.chooseChallenge')}
              </p>
            </div>

            <div>
              <div
                style={{
                  fontSize: Typography.HeadlineSmall,
                  fontWeight: Typography.WeightBold,
                  color: '#000000',
                  marginBottom: '8px'
                }}
              >
                2. {t('propFirm.tradeAndProfit')}
              </div>
              <p
                style={{
                  fontSize: Typography.BodyMedium,
                  color: 'rgba(0, 0, 0, 0.7)',
                  lineHeight: '1.65'
                }}
              >
                {t('propFirm.useVirtualCapital')}
              </p>
            </div>

            <div>
              <div
                style={{
                  fontSize: Typography.HeadlineSmall,
                  fontWeight: Typography.WeightBold,
                  color: '#000000',
                  marginBottom: '8px'
                }}
              >
                3. {t('propFirm.getFunded')}
              </div>
              <p
                style={{
                  fontSize: Typography.BodyMedium,
                  color: 'rgba(0, 0, 0, 0.7)',
                  lineHeight: '1.65'
                }}
              >
                {t('propFirm.passAndReceiveFunding')}
              </p>
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        {showConfirmModal && pendingChallenge && (
          <div
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}
            onClick={() => setShowConfirmModal(false)}
          >
            <div
              className="max-w-lg w-full p-16 border"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderColor: 'rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-12">
                <div>
                  <h3
                    style={{
                      fontSize: Typography.DisplayMedium,
                      fontWeight: Typography.WeightBold,
                      color: '#000000',
                      letterSpacing: '-0.03em',
                      marginBottom: '8px'
                    }}
                  >
                    {t('propFirm.confirmChallenge')}
                  </h3>
                  <div
                    style={{
                      fontSize: Typography.HeadlineSmall,
                      color: '#333333'
                    }}
                  >
                    {pendingChallenge.name}
                  </div>
                </div>
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="transition-colors"
                  style={{ color: 'hsla(355, 85%, 46%, 1.0)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#000000')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'hsla(355, 85%, 46%, 1.0)')}
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-6 mb-12">
                <div className="flex justify-between py-4" style={{ borderBottom: `1px solid rgba(0, 0, 0, 0.1)` }}>
                  <span style={{ color: '#333333', fontSize: Typography.BodyMedium }}>
                    {t('propFirm.entryFee')}
                  </span>
                  <span className="font-mono" style={{ color: '#000000', fontSize: Typography.BodyMedium, fontWeight: Typography.WeightBold }}>
                    ${pendingChallenge.cost.toLocaleString()}
                  </span>
                </div>

                <div className="flex justify-between py-4" style={{ borderBottom: `1px solid rgba(0, 0, 0, 0.1)` }}>
                  <span style={{ color: '#333333', fontSize: Typography.BodyMedium }}>
                    {t('propFirm.accountSize')}
                  </span>
                  <span className="font-mono" style={{ color: '#000000', fontSize: Typography.BodyMedium, fontWeight: Typography.WeightBold }}>
                    ${pendingChallenge.accountSize.toLocaleString()}
                  </span>
                </div>

                <div className="flex justify-between py-4" style={{ borderBottom: `1px solid rgba(0, 0, 0, 0.1)` }}>
                  <span style={{ color: '#333333', fontSize: Typography.BodyMedium }}>
                    {t('propFirm.yourBalance')}
                  </span>
                  <span className="font-mono" style={{ color: '#000000', fontSize: Typography.BodyMedium, fontWeight: Typography.WeightBold }}>
                    ${usdtBalance.toLocaleString()}
                  </span>
                </div>

                <div className="flex justify-between py-4">
                  <span style={{ color: '#333333', fontSize: Typography.BodyMedium }}>
                    {t('propFirm.afterPayment')}
                  </span>
                  <span className="font-mono" style={{ color: '#000000', fontSize: Typography.BodyMedium, fontWeight: Typography.WeightBold }}>
                    ${(usdtBalance - pendingChallenge.cost).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 py-4 border transition-colors"
                  style={{
                    backgroundColor: 'transparent',
                    borderColor: 'rgba(0, 0, 0, 0.2)',
                    color: '#000000',
                    borderRadius: '8px',
                    fontSize: Typography.BodyMedium,
                    fontWeight: Typography.WeightBold
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                    e.currentTarget.style.color = '#000000';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#000000';
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={confirmChallenge}
                  className="flex-1 py-4 border transition-colors"
                  style={{
                    backgroundColor: 'hsla(355, 85%, 46%, 1.0)',
                    borderColor: 'hsla(355, 85%, 46%, 1.0)',
                    color: '#FFFFFF',
                    borderRadius: '8px',
                    fontSize: Typography.BodyMedium,
                    fontWeight: Typography.WeightBold
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'hsla(355, 85%, 40%, 1.0)';
                    e.currentTarget.style.color = '#FFFFFF';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'hsla(355, 85%, 46%, 1.0)';
                    e.currentTarget.style.color = '#FFFFFF';
                  }}
                >
                  {t('propFirm.startChallenge')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Challenge Trading Interface
  return (
    <div
      className="min-h-screen p-8"
      style={{ background: 'rgb(248 250 252 / 0.95)' }}
    >
      {/* Challenge Header */}
      <div className="mb-6 p-6 border" style={{
        borderColor: 'rgba(0, 0, 0, 0.06)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: '20px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
      }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <selectedChallenge.icon size={24} style={{ color: 'hsla(355, 85%, 46%, 1.0)' }} />
            <div>
              <h1
                style={{
                  fontSize: Typography.DisplayMedium,
                  fontWeight: Typography.WeightBold,
                  color: '#000000',
                  letterSpacing: '-0.03em'
                }}
              >
                {selectedChallenge.name}
              </h1>
              <p style={{ fontSize: Typography.BodyMedium, color: 'rgba(0, 0, 0, 0.7)' }}>
                {t('propFirm.challengeInProgress')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Trading Mode Toggle */}
            <div className="flex p-1" style={{
              background: 'rgba(0, 0, 0, 0.03)',
              borderRadius: '12px',
              border: '1px solid rgba(0, 0, 0, 0.06)'
            }}>
              <button
                onClick={() => setTradingMode('crypto')}
                className="px-6 py-3 transition-all duration-300"
                style={{
                  backgroundColor: tradingMode === 'crypto' ? '#d91222' : 'transparent',
                  color: tradingMode === 'crypto' ? '#ffffff' : 'rgba(0, 0, 0, 0.6)',
                  fontSize: Typography.BodySmall,
                  fontWeight: Typography.WeightBold,
                  borderRadius: '8px',
                  boxShadow: tradingMode === 'crypto' ? '0 4px 12px rgba(217, 18, 34, 0.25)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (tradingMode !== 'crypto') {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.04)';
                    e.currentTarget.style.color = '#000000';
                  }
                }}
                onMouseLeave={(e) => {
                  if (tradingMode !== 'crypto') {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'rgba(0, 0, 0, 0.6)';
                  }
                }}
              >
                {t('propFirm.crypto')}
              </button>
              <button
                onClick={() => setTradingMode('cfd')}
                className="px-6 py-3 transition-all duration-300"
                style={{
                  backgroundColor: tradingMode === 'cfd' ? '#d91222' : 'transparent',
                  color: tradingMode === 'cfd' ? '#ffffff' : 'rgba(0, 0, 0, 0.6)',
                  fontSize: Typography.BodySmall,
                  fontWeight: Typography.WeightBold,
                  borderRadius: '8px',
                  boxShadow: tradingMode === 'cfd' ? '0 4px 12px rgba(217, 18, 34, 0.25)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (tradingMode !== 'cfd') {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.04)';
                    e.currentTarget.style.color = '#000000';
                  }
                }}
                onMouseLeave={(e) => {
                  if (tradingMode !== 'cfd') {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'rgba(0, 0, 0, 0.6)';
                  }
                }}
              >
                {t('propFirm.cfd')}
              </button>
            </div>

            {/* Pair Selector */}
            <div className="relative">
              <SearchablePairSelector
                selectedPair={selectedPair || (tradingMode === 'crypto' ? 'BTCUSDT' : 'AAPL')}
                onPairSelect={(pair: string) => {
                  setSelectedPair(pair);
                  if (pair.endsWith('USDT')) {
                    setLastSelectedPair(prev => ({...prev, crypto: pair}));
                  } else {
                    setLastSelectedPair(prev => ({...prev, cfd: pair}));
                  }
                }}
                tradingMode={tradingMode === 'cfd' ? 'cfd' : 'futures'}
              />
            </div>

            <button
              onClick={exitChallenge}
              disabled={isClosingChallenge}
              className="px-6 py-3 transition-all duration-300 disabled:opacity-40"
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #d91222',
                color: '#d91222',
                borderRadius: '12px',
                fontSize: Typography.BodySmall,
                fontWeight: Typography.WeightBold,
                boxShadow: '0 2px 8px rgba(217, 18, 34, 0.15)'
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = '#d91222';
                  e.currentTarget.style.color = '#ffffff';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(217, 18, 34, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.color = '#d91222';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(217, 18, 34, 0.15)';
                }
              }}
            >
              {isClosingChallenge ? t('propFirm.processing') : t('propFirm.exitChallenge')}
            </button>
          </div>
        </div>
      </div>

      {/* Status Messages */}
      {errorMessage && (
        <div className="mb-6 p-4 border flex items-center gap-3" style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)',
          borderColor: Editorial.Red,
          borderRadius: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}>
          <AlertTriangle size={18} style={{ color: 'hsla(355, 85%, 46%, 1.0)' }} className="flex-shrink-0" />
          <span style={{ color: '#000000', fontSize: Typography.BodyMedium }}>{errorMessage}</span>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 border flex items-center gap-3" style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)',
          borderColor: 'rgba(0, 0, 0, 0.06)',
          borderRadius: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}>
          <CheckCircle size={18} style={{ color: '#16a34a' }} className="flex-shrink-0" />
          <span style={{ color: '#000000', fontSize: Typography.BodyMedium }}>{successMessage}</span>
        </div>
      )}

      {/* Challenge Dashboard - Restructured Layout */}
      <div className="mb-6">
        {/* Top Row - Primary Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Total Account Value - Large Card */}
          <div className="p-8 border shadow-lg relative overflow-hidden group" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderColor: 'rgba(0, 0, 0, 0.06)',
            borderRadius: '16px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.08)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
          }}>
            {/* Decorative Background Shapes */}
            <div style={{
              position: 'absolute',
              top: '-30px',
              right: '-30px',
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(217, 18, 34, 0.08) 0%, transparent 70%)',
              pointerEvents: 'none'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-20px',
              left: '-20px',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(249, 115, 22, 0.06) 0%, transparent 70%)',
              pointerEvents: 'none'
            }} />

            <div className="relative z-10">
              <div className="flex items-start justify-between mb-6">
                <div
                  className="w-16 h-16 flex items-center justify-center relative"
                  style={{
                    background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.1) 0%, rgba(249, 115, 22, 0.1) 100%)',
                    borderRadius: '16px',
                    border: '2px solid rgba(217, 18, 34, 0.2)',
                    boxShadow: '0 4px 12px rgba(217, 18, 34, 0.15)'
                  }}
                >
                  <Wallet size={28} style={{ color: '#d91222' }} />
                  {/* Badge accent */}
                  <div style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #d91222 0%, #f97316 100%)',
                    border: '2px solid #ffffff'
                  }} />
                </div>

                {/* Vertical Accent Line */}
                <div style={{
                  width: '3px',
                  height: '40px',
                  background: 'linear-gradient(180deg, #d91222 0%, transparent 100%)',
                  borderRadius: '4px'
                }} />
              </div>

              <div className="mb-2">
                <div style={{
                  color: 'rgba(0, 0, 0, 0.5)',
                  fontSize: Typography.Fine,
                  textTransform: 'uppercase',
                  letterSpacing: '0.15em',
                  fontWeight: Typography.WeightBold,
                  marginBottom: '8px'
                }}>
                  {t('propFirm.totalAccountValue')}
                </div>
                <div
                  className="font-mono"
                  style={{
                    fontSize: Typography.DisplayMedium,
                    fontWeight: Typography.WeightBold,
                    color: '#000000',
                    letterSpacing: '-0.02em'
                  }}
                >
                  ${progress.currentBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4" style={{
                fontSize: Typography.BodySmall,
                borderTop: '2px solid rgba(0, 0, 0, 0.04)'
              }}>
                <div style={{ color: 'rgba(0, 0, 0, 0.5)', fontWeight: Typography.WeightMedium }}>
                  {t('propFirm.initialBalance')}
                </div>
                <div className="font-mono" style={{ color: '#000000', fontWeight: Typography.WeightBold }}>
                  ${propChallengeAccount ? propChallengeAccount.startingBalance.toLocaleString() : (selectedChallenge?.accountSize || 0).toLocaleString()}
                </div>
              </div>
            </div>
          </div>

          {/* P&L - Large Card */}
          <div className="p-8 border shadow-lg relative overflow-hidden group" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderColor: 'rgba(0, 0, 0, 0.06)',
            borderRadius: '16px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.08)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
          }}>
            {/* Decorative Grid Pattern */}
            <div style={{
              position: 'absolute',
              top: '0',
              right: '0',
              width: '100%',
              height: '100%',
              backgroundImage: `
                linear-gradient(rgba(0, 0, 0, 0.02) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0, 0, 0, 0.02) 1px, transparent 1px)
              `,
              backgroundSize: '20px 20px',
              opacity: 0.5,
              pointerEvents: 'none'
            }} />

            {/* Diagonal Accent Stripe */}
            <div style={{
              position: 'absolute',
              top: '-40px',
              right: '-40px',
              width: '150px',
              height: '150px',
              background: `linear-gradient(135deg, ${totalPnl >= 0 ? 'rgba(22, 163, 74, 0.08)' : 'rgba(217, 18, 34, 0.08)'} 0%, transparent 60%)`,
              transform: 'rotate(25deg)',
              pointerEvents: 'none'
            }} />

            <div className="relative z-10">
              <div className="flex items-start justify-between mb-6">
                <div
                  className="w-16 h-16 flex items-center justify-center relative"
                  style={{
                    background: `linear-gradient(135deg, ${totalPnl >= 0 ? 'rgba(22, 163, 74, 0.1)' : 'rgba(217, 18, 34, 0.1)'} 0%, rgba(249, 115, 22, 0.1) 100%)`,
                    borderRadius: '16px',
                    border: `2px solid ${totalPnl >= 0 ? 'rgba(22, 163, 74, 0.2)' : 'rgba(217, 18, 34, 0.2)'}`,
                    boxShadow: `0 4px 12px ${totalPnl >= 0 ? 'rgba(22, 163, 74, 0.15)' : 'rgba(217, 18, 34, 0.15)'}`
                  }}
                >
                  <TrendingUp size={28} style={{ color: totalPnl >= 0 ? '#16a34a' : '#d91222' }} />
                  {/* Star accent for positive PnL */}
                  {totalPnl >= 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '-4px',
                      right: '-4px',
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
                      border: '2px solid #ffffff'
                    }} />
                  )}
                </div>

                {/* Dynamic Status Indicator */}
                <div style={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  background: totalPnl >= 0 ? 'rgba(22, 163, 74, 0.1)' : 'rgba(217, 18, 34, 0.1)',
                  border: `1px solid ${totalPnl >= 0 ? 'rgba(22, 163, 74, 0.2)' : 'rgba(217, 18, 34, 0.2)'}`,
                  fontSize: Typography.Fine,
                  fontWeight: Typography.WeightBold,
                  color: totalPnl >= 0 ? '#16a34a' : '#d91222'
                }}>
                  {totalPnl >= 0 ? '↑ PROFIT' : '↓ LOSS'}
                </div>
              </div>

              <div className="mb-2">
                <div style={{
                  color: 'rgba(0, 0, 0, 0.5)',
                  fontSize: Typography.Fine,
                  textTransform: 'uppercase',
                  letterSpacing: '0.15em',
                  fontWeight: Typography.WeightBold,
                  marginBottom: '8px'
                }}>
                  {t('common.pnl')} ({t('propFirm.openAndClosed')})
                </div>
                <div
                  className="font-mono"
                  style={{
                    fontSize: Typography.DisplayMedium,
                    fontWeight: Typography.WeightBold,
                    color: totalPnl >= 0 ? '#16a34a' : '#d91222',
                    letterSpacing: '-0.02em'
                  }}
                >
                  {totalPnl >= 0 ? '+' : ''}{totalPnl.toFixed(2)}
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4" style={{
                fontSize: Typography.BodySmall,
                borderTop: '2px solid rgba(0, 0, 0, 0.04)'
              }}>
                <div style={{ color: 'rgba(0, 0, 0, 0.5)', fontWeight: Typography.WeightMedium }}>
                  Percentage
                </div>
                <div
                  className="font-mono"
                  style={{
                    color: progress.profitPercentage >= 0 ? '#16a34a' : '#d91222',
                    fontWeight: Typography.WeightBold
                  }}
                >
                  {progress.profitPercentage >= 0 ? '+' : ''}{progress.profitPercentage.toFixed(2)}%
                </div>
              </div>
            </div>
          </div>

          {/* Profit Target - Large Card */}
          <div className="p-8 border shadow-lg relative overflow-hidden group" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderColor: 'rgba(0, 0, 0, 0.06)',
            borderRadius: '16px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.08)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
          }}>
            {/* Concentric Circles Background */}
            <div style={{
              position: 'absolute',
              top: '50%',
              right: '-60px',
              width: '160px',
              height: '160px',
              borderRadius: '50%',
              border: '2px solid rgba(217, 18, 34, 0.08)',
              transform: 'translateY(-50%)',
              pointerEvents: 'none'
            }} />
            <div style={{
              position: 'absolute',
              top: '50%',
              right: '-40px',
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              border: '2px solid rgba(217, 18, 34, 0.12)',
              transform: 'translateY(-50%)',
              pointerEvents: 'none'
            }} />
            <div style={{
              position: 'absolute',
              top: '50%',
              right: '-20px',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(217, 18, 34, 0.1) 0%, transparent 70%)',
              transform: 'translateY(-50%)',
              pointerEvents: 'none'
            }} />

            <div className="relative z-10">
              <div className="flex items-start justify-between mb-6">
                <div
                  className="w-16 h-16 flex items-center justify-center relative"
                  style={{
                    background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.1) 0%, rgba(249, 115, 22, 0.1) 100%)',
                    borderRadius: '16px',
                    border: '2px solid rgba(217, 18, 34, 0.2)',
                    boxShadow: '0 4px 12px rgba(217, 18, 34, 0.15)'
                  }}
                >
                  <Target size={28} style={{ color: '#d91222' }} />
                  {/* Crosshair symbol */}
                  <div style={{
                    position: 'absolute',
                    top: '6px',
                    right: '6px',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    border: '2px solid #d91222',
                    background: '#ffffff'
                  }} />
                </div>

                {/* Percentage Badge */}
                <div style={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  background: 'rgba(217, 18, 34, 0.1)',
                  border: '1px solid rgba(217, 18, 34, 0.2)',
                  fontSize: Typography.Fine,
                  fontWeight: Typography.WeightBold,
                  color: '#d91222'
                }}>
                  {((progress.profit / (selectedChallenge?.profitTarget || 1)) * 100).toFixed(0)}%
                </div>
              </div>

              <div className="mb-2">
                <div style={{
                  color: 'rgba(0, 0, 0, 0.5)',
                  fontSize: Typography.Fine,
                  textTransform: 'uppercase',
                  letterSpacing: '0.15em',
                  fontWeight: Typography.WeightBold,
                  marginBottom: '8px'
                }}>
                  {t('propFirm.profitTarget')}
                </div>
                <div
                  className="font-mono"
                  style={{
                    fontSize: Typography.DisplayMedium,
                    fontWeight: Typography.WeightBold,
                    color: '#000000',
                    letterSpacing: '-0.02em'
                  }}
                >
                  ${(selectedChallenge?.profitTarget || 0).toLocaleString()}
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4" style={{
                fontSize: Typography.BodySmall,
                borderTop: '2px solid rgba(0, 0, 0, 0.04)'
              }}>
                <div style={{ color: 'rgba(0, 0, 0, 0.5)', fontWeight: Typography.WeightMedium }}>
                  Progress
                </div>
                <div className="font-mono" style={{ color: '#000000', fontWeight: Typography.WeightBold }}>
                  {((progress.profit / (selectedChallenge?.profitTarget || 1)) * 100).toFixed(1)}% {t('propFirm.complete')}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Second Row - Progress Tracking */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profit Progress Bar */}
          <div className="p-8 border shadow-lg relative overflow-hidden group" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderColor: 'rgba(0, 0, 0, 0.06)',
            borderRadius: '16px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.08)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
          }}>
            {/* Horizontal Bars Decoration */}
            <div style={{
              position: 'absolute',
              top: '20px',
              left: '0',
              right: '0',
              height: '2px',
              background: 'linear-gradient(90deg, transparent 0%, rgba(217, 18, 34, 0.1) 50%, transparent 100%)',
              pointerEvents: 'none'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '20px',
              left: '0',
              right: '0',
              height: '2px',
              background: 'linear-gradient(90deg, transparent 0%, rgba(249, 115, 22, 0.1) 50%, transparent 100%)',
              pointerEvents: 'none'
            }} />

            <div className="relative z-10">
              <div className="flex justify-between items-baseline mb-6">
                <div className="flex items-center gap-3">
                  {/* Mini Icon */}
                  <div style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #d91222 0%, #f97316 100%)',
                    boxShadow: '0 2px 8px rgba(217, 18, 34, 0.4)'
                  }} />
                  <span
                    style={{
                      fontSize: Typography.Caption,
                      color: 'rgba(0, 0, 0, 0.5)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.15em',
                      fontWeight: Typography.WeightBold
                    }}
                  >
                    {t('propFirm.profitProgress')}
                  </span>
                </div>
                <span
                  className="font-mono"
                  style={{
                    fontSize: Typography.HeadlineMedium,
                    color: '#000000',
                    fontWeight: Typography.WeightBold
                  }}
                >
                  {propChallengeAccount && selectedChallenge
                    ? Math.min(Math.max(((progress.profit / (selectedChallenge?.profitTarget || 1)) * 100), -999), 999).toFixed(1)
                    : "0.0"}%
                </span>
              </div>

              {/* Enhanced Progress Bar */}
              <div className="relative">
                <div className="w-full h-6 border" style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderColor: 'rgba(0, 0, 0, 0.08)',
                  overflow: 'hidden',
                  borderRadius: '12px',
                  position: 'relative'
                }}>
                  {/* Shimmer Effect */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 3s infinite',
                    pointerEvents: 'none'
                  }} />

                  <div
                    className="h-full transition-all duration-500"
                    style={{
                      width: `${propChallengeAccount && selectedChallenge
                        ? Math.min(Math.max((progress.profit / (selectedChallenge?.profitTarget || 1)) * 100, 0), 100)
                        : 0}%`,
                      background: 'linear-gradient(90deg, #d91222 0%, #f97316 100%)',
                      boxShadow: '0 2px 12px rgba(217, 18, 34, 0.4)',
                      position: 'relative'
                    }}
                  >
                    {/* Highlight stripe */}
                    <div style={{
                      position: 'absolute',
                      top: '4px',
                      left: '8px',
                      right: '8px',
                      height: '2px',
                      background: 'rgba(255, 255, 255, 0.3)',
                      borderRadius: '4px'
                    }} />
                  </div>
                </div>

                {/* Milestone Markers */}
                <div className="flex justify-between mt-2" style={{ fontSize: Typography.Fine, color: 'rgba(0, 0, 0, 0.7)', fontWeight: Typography.WeightMedium }}>
                  <span>0%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Drawdown Risk Bar */}
          <div className="p-8 border shadow-lg relative overflow-hidden group" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderColor: 'rgba(0, 0, 0, 0.06)',
            borderRadius: '16px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.08)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
          }}>
            {/* Zigzag Pattern */}
            <div style={{
              position: 'absolute',
              top: '0',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100px',
              height: '100px',
              background: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 10px,
                rgba(217, 18, 34, 0.03) 10px,
                rgba(217, 18, 34, 0.03) 20px
              )`,
              pointerEvents: 'none'
            }} />

            <div className="relative z-10">
              <div className="flex justify-between items-baseline mb-6">
                <div className="flex items-center gap-3">
                  {/* Warning Symbol */}
                  <div style={{
                    width: '10px',
                    height: '10px',
                    background: 'linear-gradient(135deg, #d91222 0%, #f97316 100%)',
                    transform: 'rotate(45deg)',
                    boxShadow: '0 2px 8px rgba(217, 18, 34, 0.4)'
                  }} />
                  <span
                    style={{
                      fontSize: Typography.Caption,
                      color: 'rgba(0, 0, 0, 0.5)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.15em',
                      fontWeight: Typography.WeightBold
                    }}
                  >
                    {t('propFirm.drawdownRisk')}
                  </span>
                </div>
                <span
                  className="font-mono"
                  style={{
                    fontSize: Typography.HeadlineMedium,
                    color: '#000000',
                    fontWeight: Typography.WeightBold
                  }}
                >
                  {propChallengeAccount && selectedChallenge
                    ? Math.min(((progress.drawdown / (selectedChallenge?.maxDrawdown || 1)) * 100), 999).toFixed(1)
                    : "0.0"}%
                </span>
              </div>

              {/* Enhanced Risk Bar */}
              <div className="relative">
                <div className="w-full h-6 border" style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderColor: 'rgba(0, 0, 0, 0.08)',
                  overflow: 'hidden',
                  borderRadius: '12px',
                  position: 'relative'
                }}>
                  <div
                    className="h-full transition-all duration-500"
                    style={{
                      width: `${propChallengeAccount && selectedChallenge
                        ? Math.min((progress.drawdown / (selectedChallenge?.maxDrawdown || 1)) * 100, 100)
                        : 0}%`,
                      background: 'linear-gradient(90deg, #f97316 0%, #d91222 100%)',
                      boxShadow: '0 2px 12px rgba(217, 18, 34, 0.4)',
                      position: 'relative'
                    }}
                  >
                    {/* Pulse Effect */}
                    <div style={{
                      position: 'absolute',
                      top: '0',
                      right: '0',
                      bottom: '0',
                      width: '4px',
                      background: 'rgba(255, 255, 255, 0.6)',
                      boxShadow: '0 0 8px rgba(255, 255, 255, 0.8)'
                    }} />
                  </div>

                  {/* Danger Zone Marker */}
                  <div style={{
                    position: 'absolute',
                    right: '20%',
                    top: '0',
                    bottom: '0',
                    width: '2px',
                    background: 'rgba(217, 18, 34, 0.3)',
                    pointerEvents: 'none'
                  }} />
                </div>

                {/* Risk Level Markers */}
                <div className="flex justify-between mt-2" style={{ fontSize: Typography.Fine, color: 'rgba(0, 0, 0, 0.7)', fontWeight: Typography.WeightMedium }}>
                  <span>SAFE</span>
                  <span>CAUTION</span>
                  <span>DANGER</span>
                </div>
              </div>
            </div>
          </div>

          {/* Drawdown Status Card */}
          <div className="p-8 border shadow-lg relative overflow-hidden group" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderColor: 'rgba(0, 0, 0, 0.06)',
            borderRadius: '16px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.08)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
          }}>
            {/* Triangular Pattern */}
            <div style={{
              position: 'absolute',
              bottom: '-40px',
              right: '-40px',
              width: '0',
              height: '0',
              borderLeft: '100px solid transparent',
              borderBottom: '100px solid rgba(217, 18, 34, 0.06)',
              pointerEvents: 'none'
            }} />
            <div style={{
              position: 'absolute',
              top: '-20px',
              left: '-20px',
              width: '0',
              height: '0',
              borderRight: '60px solid transparent',
              borderTop: '60px solid rgba(249, 115, 22, 0.06)',
              pointerEvents: 'none'
            }} />

            <div className="relative z-10">
              <div className="flex items-start justify-between mb-6">
                <div
                  className="w-16 h-16 flex items-center justify-center relative"
                  style={{
                    background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.1) 0%, rgba(249, 115, 22, 0.1) 100%)',
                    borderRadius: '16px',
                    border: '2px solid rgba(217, 18, 34, 0.2)',
                    boxShadow: '0 4px 12px rgba(217, 18, 34, 0.15)'
                  }}
                >
                  <AlertTriangle size={28} style={{ color: '#d91222' }} />
                  {/* Triangle Corners */}
                  <div style={{
                    position: 'absolute',
                    top: '4px',
                    left: '4px',
                    width: '6px',
                    height: '6px',
                    borderTop: '2px solid #d91222',
                    borderLeft: '2px solid #d91222'
                  }} />
                  <div style={{
                    position: 'absolute',
                    bottom: '4px',
                    right: '4px',
                    width: '6px',
                    height: '6px',
                    borderBottom: '2px solid #f97316',
                    borderRight: '2px solid #f97316'
                  }} />
                </div>

                {/* Alert Level Badge */}
                <div style={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  background: progress.drawdown > ((selectedChallenge?.maxDrawdown || 1) * 0.7)
                    ? 'rgba(217, 18, 34, 0.15)'
                    : 'rgba(22, 163, 74, 0.1)',
                  border: `1px solid ${progress.drawdown > ((selectedChallenge?.maxDrawdown || 1) * 0.7)
                    ? 'rgba(217, 18, 34, 0.3)'
                    : 'rgba(22, 163, 74, 0.2)'}`,
                  fontSize: Typography.Fine,
                  fontWeight: Typography.WeightBold,
                  color: progress.drawdown > ((selectedChallenge?.maxDrawdown || 1) * 0.7)
                    ? '#d91222'
                    : '#16a34a'
                }}>
                  {progress.drawdown > ((selectedChallenge?.maxDrawdown || 1) * 0.7) ? '⚠ HIGH' : '✓ SAFE'}
                </div>
              </div>

              <div className="mb-2">
                <div style={{
                  color: 'rgba(0, 0, 0, 0.5)',
                  fontSize: Typography.Fine,
                  textTransform: 'uppercase',
                  letterSpacing: '0.15em',
                  fontWeight: Typography.WeightBold,
                  marginBottom: '8px'
                }}>
                  {t('propFirm.drawdownStatus')}
                </div>
                <div
                  className="font-mono"
                  style={{
                    fontSize: Typography.DisplayMedium,
                    fontWeight: Typography.WeightBold,
                    color: '#000000',
                    letterSpacing: '-0.02em'
                  }}
                >
                  ${progress.drawdown.toFixed(2)}
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4" style={{
                fontSize: Typography.BodySmall,
                borderTop: '2px solid rgba(0, 0, 0, 0.04)'
              }}>
                <div style={{ color: 'rgba(0, 0, 0, 0.5)', fontWeight: Typography.WeightMedium }}>
                  Max Allowed
                </div>
                <div className="font-mono" style={{ color: '#000000', fontWeight: Typography.WeightBold }}>
                  ${(selectedChallenge?.maxDrawdown || 0).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TradingView Chart */}
      <div className="mb-6">
        <div className="border" style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderColor: 'rgba(0, 0, 0, 0.06)',
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}>
          <TradingChart selectedPair={selectedPair || (tradingMode === 'crypto' ? 'BTCUSDT' : 'AAPL')} />
        </div>
      </div>

      {/* Trading Interface */}
      <div className="space-y-6">
        {/* Trading Forms - Full Width */}
        <div>
          {tradingMode === 'cfd' ? (
            <CFDTradingForms
              availableBalance={challengeAccountBalance}
              currentPrice={currentPrice}
              usdtBalance={challengeAccountBalance}
              calculateLiquidationPrice={calculateLiquidationPrice}
              selectedPair={selectedPair || 'AAPL'}
              onCFDTrade={handleChallengeTrade}
            />
          ) : (
            <FuturesTradingForms
              availableBalance={challengeAccountBalance}
              currentBtcPrice={currentPrice}
              usdtBalance={challengeAccountBalance}
              calculateLiquidationPrice={calculateLiquidationPrice}
              onFuturesTrade={(symbol, side, amount, leverage, marginType, stopLoss, takeProfit, orderType) => {
                return handleChallengeTrade(symbol, side, amount, leverage, marginType, stopLoss, takeProfit, orderType);
              }}
              selectedPair={selectedPair || 'BTCUSDT'}
            />
          )}
        </div>

        {/* Positions - Full Width */}
        <div>
          <FuturesMyOrders
            currentBtcPrice={getSnapshotPriceBySymbol('BTCUSDT') || currentPrice}
            futuresPositions={propPositionsWithLiveData}
            marketData={marketData}
            onClosePosition={onClosePropPosition}
            openOrders={propOrders}
            onCancelOrder={onCancelPropOrder}
            onCancelAllOrders={onCancelAllPropOrders}
            propHistory={propChallengeAccount ? propPositionHistory.filter(history =>
              history.challengeId === propChallengeAccount.challengeId
            ) : []}
            challengeId={propChallengeAccount?.challengeId}
            selectedPair={selectedPair}
            tradingMode="futures"
            currentSelectedPairPrice={currentPrice}
            realtimeConnected={realtimeConnected}
          />
        </div>
      </div>

      {/* Challenge Outcome Modal */}
      {showChallengeOutcomeModal && challengeOutcome && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}
        >
          <div
            className="max-w-lg w-full p-16 border"
            style={{
              backgroundColor: Editorial.Paper,
              borderColor: Editorial.Ink,
              borderRadius: BorderRadius.None
            }}
          >
            <div className="text-center mb-12">
              {challengeOutcome.status === 'won' ? (
                <Trophy size={48} style={{ color: 'hsla(355, 85%, 46%, 1.0)', margin: '0 auto 16px' }} />
              ) : (
                <AlertTriangle size={48} style={{ color: 'hsla(355, 85%, 46%, 1.0)', margin: '0 auto 16px' }} />
              )}

              <h3
                style={{
                  fontSize: Typography.DisplayMedium,
                  fontWeight: Typography.WeightBold,
                  color: Editorial.Ink,
                  letterSpacing: '-0.03em',
                  marginBottom: '16px'
                }}
              >
                {challengeOutcome.status === 'won' ? t('propFirm.challengeCompleted') :
                 challengeOutcome.status === 'lost' ? t('propFirm.challengeFailed') :
                 challengeOutcome.status === 'expired' ? t('propFirm.challengeExpired') : t('propFirm.challengeCancelled')}
              </h3>

              <p
                style={{
                  fontSize: Typography.BodyMedium,
                  color: Editorial.Ink,
                  lineHeight: '1.6'
                }}
              >
                {challengeOutcome.message}
              </p>
            </div>

            <div className="space-y-4 mb-12">
              <div className="flex justify-between py-4" style={{ borderBottom: `1px solid ${Editorial.Ink}` }}>
                <span style={{ color: Editorial.Ink, fontSize: Typography.BodyMedium }}>
                  {t('propFirm.finalPnl')}
                </span>
                <span
                  className="font-mono"
                  style={{
                    fontSize: Typography.BodyMedium,
                    fontWeight: Typography.WeightBold,
                    color: challengeOutcome.profit >= 0 ? Editorial.Ink : Editorial.Red
                  }}
                >
                  {challengeOutcome.profit >= 0 ? '+' : ''}{challengeOutcome.profit.toFixed(2)} USD
                </span>
              </div>

              {challengeOutcome.status === 'won' && (
                <div className="flex justify-between py-4">
                  <span style={{ color: Editorial.Ink, fontSize: Typography.BodyMedium }}>
                    {t('propFirm.rewardAdded')}
                  </span>
                  <span
                    className="font-mono"
                    style={{
                      fontSize: Typography.BodyMedium,
                      fontWeight: Typography.WeightBold,
                      color: Editorial.Ink
                    }}
                  >
                    +{(selectedChallenge?.profitTarget || 0).toFixed(2)} USD
                  </span>
                </div>
              )}
            </div>

            <button
              onClick={handleCloseOutcomeModal}
              disabled={isClosingChallenge}
              className="w-full py-4 border-2 transition-colors disabled:opacity-40"
              style={{
                backgroundColor: Editorial.Paper,
                borderColor: '#d91222',
                color: '#d91222',
                borderRadius: '12px',
                fontSize: Typography.BodyMedium,
                fontWeight: Typography.WeightBold
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = '#d91222';
                  e.currentTarget.style.color = Editorial.Paper;
                }
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = Editorial.Paper;
                  e.currentTarget.style.color = '#d91222';
                }
              }}
            >
              {isClosingChallenge ? t('propFirm.processing') : t('propFirm.closeChallenge')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropFirmChallengePage;
